import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/shared/GlassCard';
import Button from '../components/shared/Button';
import Toggle from '../components/shared/Toggle';
import AccentPicker from '../components/shared/AccentPicker';
import Icon from '../components/shared/Icon';
import ActivityHeatmap from '../components/shared/ActivityHeatmap';
import { MOCK_COACHES, MOCK_LOGS, MOCK_WORKOUTS } from '../utils/mockData';
import './ProfilePage.css';

function EditableField({ label, value, onSave, type = 'text', placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  function handleSave() {
    onSave(draft.trim());
    setEditing(false);
  }

  function handleCancel() {
    setDraft(value || '');
    setEditing(false);
  }

  return (
    <div className="profile-page__field-row">
      <div className="profile-page__field-label">{label}</div>
      {editing ? (
        <div className="profile-page__field-edit">
          <input
            className="profile-page__field-input"
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder || label}
            autoFocus
          />
          <div className="profile-page__field-actions">
            <button className="profile-page__field-save" onClick={handleSave}>Save</button>
            <button className="profile-page__field-cancel" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="profile-page__field-view">
          <span className="profile-page__field-value">{value || <span className="profile-page__field-empty">Not set</span>}</span>
          <button className="profile-page__field-edit-btn" onClick={() => setEditing(true)} aria-label={`Edit ${label}`}>
            <Icon name="edit" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function EmailField({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(value || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  function handleSave() {
    if (!email.trim() || !email.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Enter your current password to confirm.');
      return;
    }
    setError(null);
    onSave(email.trim());
    setPassword('');
    setEditing(false);
  }

  function handleCancel() {
    setEmail(value || '');
    setPassword('');
    setError(null);
    setEditing(false);
  }

  return (
    <div className="profile-page__field-row">
      <div className="profile-page__field-label">Email</div>
      {editing ? (
        <div className="profile-page__field-edit">
          <input
            className="profile-page__field-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="New email address"
            autoFocus
          />
          <input
            className="profile-page__field-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Current password to confirm"
          />
          {error && <span className="profile-page__field-error">{error}</span>}
          <p className="profile-page__reauth-hint">Enter your current password to confirm email change.</p>
          <div className="profile-page__field-actions">
            <button className="profile-page__field-save" onClick={handleSave}>Save</button>
            <button className="profile-page__field-cancel" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="profile-page__field-view">
          <span className="profile-page__field-value">{value}</span>
          <button className="profile-page__field-edit-btn" onClick={() => setEditing(true)} aria-label="Edit email">
            <Icon name="edit" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);

  const [remindersEnabled, setRemindersEnabled] = useState(
    () => localStorage.getItem('fitndex_reminders') === 'true'
  );

  const initials = user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  const coach = user.coachId ? MOCK_COACHES[user.coachId] : null;

  const activityEntries = useMemo(() => {
    if (user.role === 'coach') {
      const teamIds = user.team || [];
      const logs = MOCK_LOGS
        .filter((log) => teamIds.includes(log.athleteId))
        .map((log) => ({ date: log.date, value: 2 }));
      const assigned = MOCK_WORKOUTS
        .filter((workout) => workout.createdBy === user.id)
        .map((workout) => ({ date: workout.scheduledDate, value: 1 }));
      return [...logs, ...assigned];
    }

    const logs = MOCK_LOGS
      .filter((log) => log.athleteId === user.id)
      .map((log) => ({ date: log.date, value: 2 }));
    const assigned = MOCK_WORKOUTS
      .filter((workout) => workout.assignedTo.includes(user.id))
      .map((workout) => ({ date: workout.scheduledDate, value: 1 }));

    return [...logs, ...assigned];
  }, [user]);

  function formatLastActive(iso) {
    if (!iso) return 'Active now';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 2) return 'Active now';
    if (mins < 60) return `Active ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Active ${hrs}h ago`;
    return `Active ${Math.floor(hrs / 24)}d ago`;
  }

  async function handleRemindersToggle() {
    if (!remindersEnabled) {
      try {
        const permission = await Notification.requestPermission();
        const granted = permission === 'granted';
        setRemindersEnabled(granted);
        localStorage.setItem('fitndex_reminders', String(granted));
      } catch {
        setRemindersEnabled(false);
        localStorage.setItem('fitndex_reminders', 'false');
      }
    } else {
      setRemindersEnabled(false);
      localStorage.setItem('fitndex_reminders', 'false');
    }
  }

  function handleAvatarChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updateUser({ avatar: ev.target.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function handleLogout() {
    logout();
    navigate('/');
  }

  return (
    <div className="profile-page">
      <div className="profile-page__header">
        <h1 className="profile-page__title">Profile</h1>
      </div>

      <GlassCard size="md" className="profile-page__identity">
        <div className="profile-page__avatar-wrap" onClick={() => avatarInputRef.current?.click()}>
          {user.avatar
            ? <img src={user.avatar} alt="Avatar" className="profile-page__avatar-img" />
            : <div className="profile-page__avatar">{initials}</div>
          }
          <div className="profile-page__avatar-overlay" aria-hidden="true">
            <Icon name="camera" size={18} />
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            className="profile-page__avatar-input"
            onChange={handleAvatarChange}
          />
        </div>
        <div className="profile-page__info">
          <p className="profile-page__name">{user.name}</p>
          <p className="profile-page__email">{user.email}</p>
          {user.lastActive && (
            <p className="profile-page__last-active">{formatLastActive(user.lastActive)}</p>
          )}
          <span className={`profile-page__role-badge profile-page__role-badge--${user.role}`}>
            {user.role}
          </span>
        </div>
      </GlassCard>

      <GlassCard size="md" className="profile-page__settings-card profile-page__activity-card">
        <h2 className="profile-page__section-title">Activity</h2>
        <ActivityHeatmap
          entries={activityEntries}
          title={user.role === 'coach' ? 'Team activity' : 'Personal activity'}
          subtitle="Tracked over the last 52 weeks"
        />
      </GlassCard>

      {coach && (
        <GlassCard size="md" className="profile-page__settings-card">
          <h2 className="profile-page__section-title">Coach</h2>
          <div className="profile-page__coach-row">
            <div className="profile-page__coach-avatar">
              {coach.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div className="profile-page__coach-info">
              <p className="profile-page__coach-name">{coach.name}</p>
              <p className="profile-page__coach-specialty">{coach.specialty}</p>
            </div>
          </div>
        </GlassCard>
      )}

      <GlassCard size="md" className="profile-page__settings-card">
        <h2 className="profile-page__section-title">Account</h2>
        <EmailField
          value={user.email}
          onSave={(val) => updateUser({ email: val })}
        />
        <EditableField
          label="Phone"
          value={user.phoneNumber}
          onSave={(val) => updateUser({ phoneNumber: val })}
          type="tel"
          placeholder="+1 (555) 000-0000"
        />
        <EditableField
          label="Home Gym"
          value={user.homeGym}
          onSave={(val) => updateUser({ homeGym: val })}
          placeholder="Gym name or location"
        />
        <EditableField
          label="Workout Time"
          value={user.workoutTime}
          onSave={(val) => updateUser({ workoutTime: val })}
          placeholder="e.g. 6:00 AM"
        />
      </GlassCard>

      <GlassCard size="md" className="profile-page__settings-card">
        <h2 className="profile-page__section-title">Appearance</h2>

        <div className="profile-page__setting-row">
          <div className="profile-page__setting-label">
            <p className="profile-page__setting-name">Dark Mode</p>
            <p className="profile-page__setting-hint">Toggle dark and light theme</p>
          </div>
          <Toggle />
        </div>

        <div className="profile-page__setting-row profile-page__setting-row--col">
          <div className="profile-page__setting-label">
            <p className="profile-page__setting-name">Accent Color</p>
            <p className="profile-page__setting-hint">Personalize your color scheme</p>
          </div>
          <AccentPicker />
        </div>
      </GlassCard>

      <GlassCard size="md" className="profile-page__settings-card">
        <h2 className="profile-page__section-title">Reminders</h2>
        <div className="profile-page__setting-row">
          <div className="profile-page__setting-label">
            <p className="profile-page__setting-name">Workout Reminders</p>
            <p className="profile-page__setting-hint">
              {remindersEnabled ? 'Browser notifications on' : 'Get notified before scheduled workouts'}
            </p>
          </div>
          <button
            className={`profile-page__reminder-toggle ${remindersEnabled ? 'profile-page__reminder-toggle--on' : ''}`}
            onClick={handleRemindersToggle}
            type="button"
            aria-pressed={remindersEnabled}
            aria-label="Toggle workout reminders"
          >
            <span className="profile-page__reminder-thumb" />
          </button>
        </div>
      </GlassCard>

      <Button variant="danger" size="md" onClick={handleLogout} className="profile-page__logout">
        Sign Out
      </Button>
    </div>
  );
}

